import { FC, useEffect, useMemo, useState } from 'react';
import { INextChallengeBanner } from 'blades/nextChallengeBannerBlade/@types';
import ImageComponent from 'components/Image';
import Button from 'components/Button';
import styles from './nextChallengeBanner.module.sass';
import { getNextChallenge } from '../../helpers';

const timeDifference = (date?: string) => {
  if (!date) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  const now = new Date();
  const targetDate = new Date(date);
  const difference = targetDate.getTime() - now.getTime();
  const days = Math.floor(difference / (1000 * 60 * 60 * 24));
  const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((difference % (1000 * 60)) / 1000);
  return { days, hours, minutes, seconds };
};

const NextChallengeBanner: FC<INextChallengeBanner> = props => {
  const { title, description, icon, date, useNextChallenge, link } = props;
  const [mounted, setMounted] = useState(false);
  const [time, setTime] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  useEffect(() => {
    setMounted(true);
    if (date && !useNextChallenge) {
      setTime(timeDifference(date));
      let interval: string;

      setTimeout(
        () => {
          interval = setInterval(() => {
            setTime(timeDifference(date));
          }, 1000 * 60) as unknown as string;
        },
        timeDifference(date).seconds * 1000,
      );

      return () => clearInterval(interval);
    }

    if (useNextChallenge) {
      const fetchNextChallenge = async () => {
        const nextChallenge = await getNextChallenge();
        const launchDate = nextChallenge?.fields?.launchDate;

        setTime(timeDifference(launchDate));

        let interval: string;

        setTimeout(
          () => {
            interval = setInterval(() => {
              setTime(timeDifference(launchDate));
            }, 1000 * 60) as unknown as string;
          },
          timeDifference(launchDate).seconds * 1000,
        );

        return () => clearInterval(interval);
      };
      fetchNextChallenge();
    }
    return undefined;
  }, [date, useNextChallenge]);

  const ImageMemo = useMemo(() => {
    if (icon?.fields?.file?.url) {
      return (
        <span className={styles.iconWrapper}>
          <ImageComponent image={icon?.fields?.file?.url as string} />
        </span>
      );
    }
    return null;
  }, [icon]);

  if (!mounted) {
    return null; // or a loading state
  }

  return (
    <div className={styles.nextChallengeBanner}>
      <div className="container">
        <h3 className={styles.title}>
          {ImageMemo}
          <span>{title}</span>
        </h3>
        <div className={styles.countdown}>
          <div className={styles.countdownItem}>
            <span className={styles.countdownItemNumber}>{time.days}</span>
            <span className={styles.countdownItemText}>DAYS</span>
          </div>
          <div className={styles.countdownItem}>
            <span className={styles.countdownItemNumber}>{time.hours}</span>
            <span className={styles.countdownItemText}>HOURS</span>
          </div>
          <div className={styles.countdownItem}>
            <span className={styles.countdownItemNumber}>{time.minutes}</span>
            <span className={styles.countdownItemText}>MINS</span>
          </div>
        </div>
        <p className={styles.description}>{description}</p>
        {link && <Button link={link} />}
      </div>
    </div>
  );
};

export default NextChallengeBanner;
